import { useState } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  Input,
  Select,
  Table,
  Badge,
  Modal,
  LoadingSpinner,
  ErrorSummary,
  EmptyState,
} from '@/components/common';
import { useBulkSelection, BulkActionBar, BulkSelectCheckbox, BulkSelectAllCheckbox } from '@/components/bulk';
import { applyServerErrors } from '@/utils/formErrors';
import { phoneError } from '@/utils/validation';
import {
  useRTOs,
  useCreateRTO,
  useUpdateRTO,
  useDeleteRTO,
  useToggleRTOActive,
  useStateLookup,
  useCityLookup,
} from '@/hooks/useGeography';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import type { RTO, RTORequest } from '@/services/geography';

const zoneOptions = [
  { value: '', label: 'All Zones' },
  { value: 'A', label: 'Zone A' },
  { value: 'B', label: 'Zone B' },
  { value: 'C', label: 'Zone C' },
];

// Item 21: Status filter. is_active is a plain boolean on the model/filterset
// (RTOViewSet.filterset_fields already had it before this batch); the values
// here are the strings buildParams() sends and django-filter's BooleanFilter
// already accepts.
const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const zoneColors: Record<string, 'primary' | 'success' | 'warning'> = {
  A: 'primary',
  B: 'success',
  C: 'warning',
};

export function RTOsPage() {
  const [page, setPage] = useState(1);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `search` drives the Input synchronously, `debouncedSearch` (300ms) is
  // what actually feeds the query below.
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  const [stateFilter, setStateFilter] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  // Item 21: City and Status filters, ADDED alongside State (not replacing
  // it -- see the file-level comment above the Filters block below for why).
  const [cityFilter, setCityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRTO, setEditingRTO] = useState<RTO | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data: statesData } = useStateLookup();
  const { data: citiesData } = useCityLookup(stateFilter || undefined);
  const { data, isLoading, isError, error, refetch } = useRTOs(page, {
    search: debouncedSearch,
    state: stateFilter || undefined,
    city: cityFilter || undefined,
    zone: zoneFilter || undefined,
    is_active: statusFilter === '' ? undefined : statusFilter === 'true',
  });
  const createMutation = useCreateRTO();
  const updateMutation = useUpdateRTO();
  const deleteMutation = useDeleteRTO();
  const toggleActiveMutation = useToggleRTOActive();

  // Item 21: multi-select + bulk status change/delete via the Wave 0 kit.
  const visibleIds = (data?.results || []).map((rto) => rto.id);
  const bulkSelection = useBulkSelection(visibleIds);

  const [formData, setFormData] = useState<RTORequest>({
    state: '',
    code: '',
    name: '',
    zone: 'B',
    is_active: true,
  });

  const { data: formCitiesData } = useCityLookup(formData.state || undefined);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [summaryMessages, setSummaryMessages] = useState<string[]>([]);

  const stateOptions = [
    { value: '', label: 'All States' },
    ...(statesData?.map(s => ({ value: s.id, label: s.name })) || []),
  ];

  // Item 21: City filter options, cascading from the State filter exactly
  // like the create/edit form's own City field does (useCityLookup(state)).
  const cityOptions = [
    { value: '', label: 'All Cities' },
    ...(citiesData?.map(c => ({ value: c.id, label: c.name })) || []),
  ];

  const handleOpenModal = (rto?: RTO) => {
    if (rto) {
      setEditingRTO(rto);
      setFormData({
        state: rto.state,
        city: rto.city || undefined,
        code: rto.code,
        name: rto.name,
        address: rto.address || '',
        phone: rto.phone || '',
        email: rto.email || '',
        zone: rto.zone,
        is_active: rto.is_active,
      });
    } else {
      setEditingRTO(null);
      setFormData({
        state: stateFilter || '',
        code: '',
        name: '',
        zone: 'B',
        is_active: true,
      });
    }
    setFieldErrors({});
    setSummaryMessages([]);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setSummaryMessages([]);
    // Phone is optional on an RTO, but when provided it must be a valid
    // 10-digit number (no country code — derived server-side).
    if (formData.phone && formData.phone.trim()) {
      const phoneMsg = phoneError(formData.phone);
      if (phoneMsg) {
        setFieldErrors({ phone: phoneMsg });
        return;
      }
    }
    try {
      if (editingRTO) {
        await updateMutation.mutateAsync({ id: editingRTO.id, data: formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
      setIsModalOpen(false);
    } catch (error) {
      const nextErrors: Record<string, string> = {};
      const messages = applyServerErrors(error, (name, err) => {
        nextErrors[name as string] = err.message;
      });
      setFieldErrors(nextErrors);
      setSummaryMessages(messages);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
    setDeleteConfirm(null);
  };

  const columns = [
    {
      key: 'select',
      header: '',
      // See the "select all" strip above the table for the header checkbox --
      // Table's column header is plain text, so it can't live in the <th>.
      render: (rto: RTO) => (
        <BulkSelectCheckbox
          checked={bulkSelection.isSelected(rto.id)}
          onChange={() => bulkSelection.toggle(rto.id)}
          label={`Select ${rto.code}`}
        />
      ),
    },
    {
      key: 'code',
      header: 'RTO Code',
      render: (rto: RTO) => (
        <span className="font-mono font-medium text-gray-900 dark:text-gray-100">{rto.code}</span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      // Long office names wrap instead of widening the table past the card (R2-F4).
      className: 'whitespace-normal min-w-[12rem]',
      render: (rto: RTO) => rto.name,
    },
    {
      key: 'state',
      header: 'State',
      render: (rto: RTO) => rto.state_name,
    },
    {
      key: 'city',
      header: 'City',
      render: (rto: RTO) => rto.city_name || '-',
    },
    {
      key: 'zone',
      header: 'Zone',
      render: (rto: RTO) => (
        <Badge variant={zoneColors[rto.zone]}>Zone {rto.zone}</Badge>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (rto: RTO) => (
        <Badge variant={rto.is_active ? 'success' : 'error'}>
          {rto.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (rto: RTO) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => toggleActiveMutation.mutate(rto.id)}
            className={`px-2 py-1 text-xs rounded ${
              rto.is_active
                ? 'bg-red-50 text-red-600 hover:bg-red-100'
                : 'bg-green-50 text-green-600 hover:bg-green-100'
            }`}
          >
            {rto.is_active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => handleOpenModal(rto)}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => setDeleteConfirm(rto.id)}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError) {
    // A failed request must never render as "no RTOs" -- see StatesPage's
    // identical guard for the same reasoning.
    return (
      <div className="space-y-6">
        <PageHeader title="RTOs" subtitle="Manage Regional Transport Offices" />
        <Card>
          <EmptyState
            icon={<ExclamationTriangleIcon />}
            title="Could not load RTOs"
            description={error instanceof Error ? error.message : 'The request failed. Please try again.'}
            action={
              <button
                type="button"
                onClick={() => refetch()}
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Try again
              </button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="RTOs"
        subtitle="Manage Regional Transport Offices"
        actions={
          <Button onClick={() => handleOpenModal()}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Add RTO
          </Button>
        }
      />

      <Card>
        <div className="p-4 border-b border-gray-200">
          {/*
            Item 21 asked for City and Status filters. State and Zone stayed:
            an RTO belongs to a city WITHIN a state, so City replacing State
            would leave City holding the entire country's offices with no way
            to narrow it -- the opposite of usable. City is instead ADDED
            alongside State and cascades from it (disabled until a state is
            picked), the same pattern City already uses under State on the
            Cities and Pincodes admin pages above. Status is the plain
            is_active boolean the model already has; RTOViewSet.filterset_fields
            already accepted it before this batch, so only the control was
            missing.
          */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4">
            <div className="flex-1 min-w-[12rem]">
              <Input
                placeholder="Search RTOs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
              />
            </div>
            <div className="w-full sm:w-44">
              <Select
                options={stateOptions}
                value={stateFilter}
                onChange={(e) => {
                  setStateFilter(e.target.value);
                  setCityFilter('');
                }}
              />
            </div>
            <div className="w-full sm:w-44">
              <Select
                options={cityOptions}
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                disabled={!stateFilter}
              />
            </div>
            <div className="w-full sm:w-32">
              <Select
                options={zoneOptions}
                value={zoneFilter}
                onChange={(e) => setZoneFilter(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-36">
              <Select
                options={statusOptions}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              />
            </div>
          </div>
        </div>

        {visibleIds.length > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60">
            <BulkSelectAllCheckbox
              checked={bulkSelection.allVisibleSelected}
              indeterminate={bulkSelection.someVisibleSelected}
              onChange={bulkSelection.toggleAllVisible}
              label="Select all RTOs on this page"
            />
            <span className="text-sm text-gray-600 dark:text-gray-300">Select all on this page</span>
          </div>
        )}

        {bulkSelection.count > 0 && (
          <div className="px-4 pt-3">
            <BulkActionBar
              resource="geography/rtos"
              selectedIds={bulkSelection.selectedIds}
              entityName="RTOs"
              onChanged={() => refetch()}
              onClear={bulkSelection.clear}
            />
          </div>
        )}

        <Table
          compact
          columns={columns}
          data={data?.results || []}
          emptyMessage="No RTOs found"
          rowKey={(rto) => rto.id}
        />

        {data && data.count > 20 && (
          <div className="p-4 border-t border-gray-200 flex justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, data.count)} of {data.count}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={!data.next}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingRTO ? 'Edit RTO' : 'Add RTO'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <ErrorSummary messages={summaryMessages} />
          <Select
            label="State"
            options={statesData?.map(s => ({ value: s.id, label: s.name })) || []}
            value={formData.state}
            onChange={(e) => setFormData({ ...formData, state: e.target.value, city: undefined })}
            error={fieldErrors.state}
            required
          />
          <Select
            label="City (Optional)"
            options={[
              { value: '', label: 'Select City' },
              ...(formCitiesData?.map(c => ({ value: c.id, label: c.name })) || []),
            ]}
            value={formData.city || ''}
            onChange={(e) => setFormData({ ...formData, city: e.target.value || undefined })}
            disabled={!formData.state}
            error={fieldErrors.city}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="RTO Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              placeholder="e.g., MH01"
              error={fieldErrors.code}
              required
            />
            <Select
              label="Zone"
              options={[
                { value: 'A', label: 'Zone A' },
                { value: 'B', label: 'Zone B' },
                { value: 'C', label: 'Zone C' },
              ]}
              value={formData.zone}
              onChange={(e) => setFormData({ ...formData, zone: e.target.value as 'A' | 'B' | 'C' })}
              error={fieldErrors.zone}
            />
          </div>
          <Input
            label="RTO Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Mumbai Central"
            error={fieldErrors.name}
            required
          />
          <Input
            label="Address"
            value={formData.address || ''}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            error={fieldErrors.address}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Phone"
              value={formData.phone || ''}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="10-digit number"
              error={fieldErrors.phone}
            />
            <Input
              label="Email"
              type="email"
              value={formData.email || ''}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              error={fieldErrors.email}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="rounded border-gray-300"
            />
            <label htmlFor="is_active" className="text-sm text-gray-700 dark:text-gray-300">
              Active
            </label>
          </div>
          {fieldErrors.is_active && (
            <p className="text-sm text-red-600">{fieldErrors.is_active}</p>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>
              {editingRTO ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete RTO"
      >
        <p className="text-gray-600 dark:text-gray-300">
          Are you sure you want to delete this RTO?
        </p>
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            isLoading={deleteMutation.isPending}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}
